<template>
  <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
    <h1 class="invoice-title text-center py-1">INVOICE</h1>
    <div class="header mb-3">
      <div class="d-flex justify-content-between">
        <div class="one-third-block"></div>

        <div class="one-third-block">
          <div class="text-end mb-2">
            <div class="text-end flex-end">
              <!-- 請求年と請求月 -->
              <input v-model="localData.billing_year" type="hidden">
              <input v-model="localData.billing_month" type="hidden">
            </div>
            <div>
              <input
                v-model="localData.billing_dt"
                type="date"
                class="form-control"
                max="2099-12-31"
                required
              >
            </div>
          </div>
          <p class="text-end mb-0">Invoice No. {{ localData.parent_billing_no }}</p>
        </div>
      </div>

      <div class="d-flex justify-content-between mb-3">
        <div class="annual-bill-customer-rep mb-0 d-flex">
          <!-- 顧客名(請求先) 御中 -->
          <div class="mb-3">
            <div class="d-flex mb-1">
              <div class="mt-1">
                <span class="text-nowrap me-2">Bill To:</span>
              </div>
              <div>
                <div>
                  <CustomerSelectBox
                    v-model="localData.m_customer_id"
                    :options="customers"
                    :required="true"
                  />
                </div>
                <!-- 顧客住所 -->
                <label for="billing_customer_address_display_flg" class="ECM_CheckboxInput">
                  <input
                    v-model="localData.billing_customer_address_display_flg"
                    :true-value="true"
                    :false-value="false"
                    id="billing_customer_address_display_flg"
                      type="checkbox"
                      class="ECM_CheckboxInput-Input"
                    >
                    <span class="ECM_CheckboxInput-DummyInput"></span>
                    <h4 class="mx-2">顧客住所表示</h4>
                </label>
                <div v-if="localData.billing_customer_address_display_flg === true">
                  <p v-html="(localData.customer_address || '').replace(/\n/g, '<br>')" class="mb-0 text-start"></p>
                </div>
              </div>
            </div>
            <div class="text-danger">※顧客担当者マスタ登録がある場合のみ、自動反映されます</div>
          </div>
        </div>
      </div>

      <div class="middle-title d-flex justify-content-between">
        <div></div>
        <div class="text-end">
          <div class="company-info-block ">
            <h4 class="billing-company-nm mb-0">
              <span class="color-billing-company-orange">S</span>oei 
              <span class="color-billing-company-orange">P</span>lanning 
              <span class="color-billing-company-orange">C</span>o., Ltd.
            </h4>
            <p class="billing-company-address mb-0 text-start"><span class="me-4">2-38-6</span><span>Yoyogi, Shibuya-ku,</span></p>
            <p class="billing-company-address mb-0 text-start"><span class="me-4">Tokyo,</span><span>151-0053</span></p>
            <p class="billing-company-address mb-0 text-end"><span>Tel：03-3370-8006</span></p>
            <p class="billing-company-address mb-0 text-end"><span>Fax：03-3370-8157</span></p>
          </div>
        </div>
      </div>
    </div>

    <div class="total-billing-amount-area mb-2">
      <div>
        <h1 class="total-billing-amount">Total Invoice Amount {{ formatCurrency(calculateGrandTotal()) }}</h1>
      </div>
      <div class="d-flex align-items-center carryover-amount">
        <h5 class="mb-0 me-2">Carryover Amount</h5>
        <div class="d-flex align-items-center">
          <CommaNumberInput
            v-model="localData.carryover_amount"
            placeholder="Carryover Amount"
            @input="updateTotals"
            class="form-control"
            id="carryover_amount_input"
          />
        </div>
      </div>
    </div>

    <!-- 業務内容のテーブル -->
    <div>
      <table class="table table-bordered monthly-detail-data">
        <thead>
          <tr>
            <th class="invoice-title text-nowrap">ID<br><span class="text-danger">臨時作業のみ</span></th>
            <th class="invoice-title text-nowrap">Building</th>
            <th class="invoice-title work-date">Date<br><span class="text-danger">業者/顧客予定表の作業日がある場合、日付が入ります</span></th>
            <th class="invoice-title text-nowrap">Description</th>
            <th class="invoice-title text-nowrap">Breakdown</th>
            <th class="invoice-title text-nowrap bill-amount">Amount</th>
            <th class="invoice-title text-nowrap">操作</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="localData?.t_customer_monthly_billing_details">
            <tr
              v-for="(billing, index) in localData.t_customer_monthly_billing_details"
              :key="billing.client_row_key || index"
              :class="{ 'annual-work-cancel-row': billing.cancel_annual_work }"
            >
              <!-- 請求番号 -->
              <td class="text-nowrap">{{ billing.billing_no }}</td>

              <!-- 建物列：最初の1回だけ表示して rowspan -->
              <!-- ▼ 建物列：分岐ロジックを整理 -->
              <template v-if="billing.work_type == 2">
                <!-- 追加行はそのままセレクトボックス表示（rowspanなし） -->
                <td class="text-start">
                  <BuildingSelectBox
                    v-model="billing.m_building_id"
                    :options="buildings"
                  />
                </td>
              </template>
              <template
                v-else-if="groupedBillings[index]"
              >
                <!-- 初期表示の行、かつ最初の行のみ rowspan -->
                <td
                  class="text-start"
                  :rowspan="groupedBillings[index].count"
                >
                  <input type="hidden" v-model="billing.m_building_id" />
                  {{ billing.m_building?.building_nm || '' }}
                </td>
              </template>

              <!-- 初期行だけど rowspan 対象ではない（tdスキップ） -->
              <!-- tdなし（空）＝rowspanに含まれている行 -->

              <!-- 作業日と作業年月 -->
              <td class="work-date">
                <input
                  v-model="billing.work_dt"
                  type="date"
                  class="form-control"
                  max="2099-12-31"
                  :disabled="billing.cancel_annual_work"
                >
                <input v-model="billing.work_year" type="hidden">
                <input v-model="billing.work_month" type="hidden">
              </td>

              <!-- 作業内容 -->
              <td>
                <span v-if="billing.t_building_work_schedule_detail_id" class="multiline-text text-nowrap">
                  {{ stripMonthlyBillingLabel(billing.work_content) }}
                </span>
                <textarea
                  v-else
                  :value="stripMonthlyBillingLabel(billing.work_content)"
                  class="form-control"
                  rows="2"
                  :disabled="billing.cancel_annual_work"
                  @input="billing.work_content = $event.target.value"
                  @keydown.enter.stop
                ></textarea>
              </td>

              <!-- 内訳 -->
              <td>
                <textarea
                  v-model="billing.breakdown"
                  class="form-control"
                  rows="2"
                  :disabled="billing.cancel_annual_work"
                  @keydown.enter.stop
                ></textarea>
              </td>

              <!-- 請求金額 -->
              <td class="bill-amount">
                <div class="d-flex align-items-center">
                  <CommaNumberInput
                    v-model="billing.bill_amount"
                    placeholder="請求金額(税抜)"
                    class="form-control"
                    :disabled="billing.cancel_annual_work"
                  />
                </div>
              </td>

              <!-- 年間予定作業は中止予定をトグル、追加行は単純削除 -->
              <td>
                <span
                  v-if="billing.fixed_fee_summary"
                  class="fixed-fee-summary-badge"
                  title="予定作業を中止しても、この定額請求の金額は維持されます"
                >定額</span>
                <label
                  v-else-if="billing.work_type === 0 && billing.annual_work_cancellation_allowed"
                  class="annual-cancel-check"
                  :title="billing.fixed_fee_billing ? '作業明細を除外して予定を中止します。定額請求の金額は変わりません' : '作業明細を請求から除外して予定を中止します'"
                >
                  <input v-model="billing.cancel_annual_work" type="checkbox" class="form-check-input">
                  <span>{{ billing.cancel_annual_work ? '中止予定' : '中止' }}</span>
                </label>
                <button
                  v-else-if="billing.work_type === 2"
                  type="button"
                  class="btn btn-danger btn-sm text-nowrap"
                  @click="removeBillingRow(index)"
                >
                  削除
                </button>
              </td>
            </tr>
            <tr>
              <td colspan="7" class="text-center">
                <button
                  type="button"
                  class="btn btn-info btn-sm mx-2"
                  @click="addDefaultRows"
                >
                  デフォルトの2項目を追加
                </button>

                <button type="button" class="btn btn-primary btn-sm mx-2"  @click="$emit('add-row')">請求項目を追加</button>
              </td>
            </tr>
          <tr>
            <td colspan="5" class="invoice-title">Sub Total</td>
            <td class="text-end">
              {{ formatCurrency(localData.total_amount) }}
              <input type="hidden" v-model="localData.total_amount" readonly class="form-control">
            </td>
            <td></td>
          </tr>
          <tr>
            <td colspan="5" class="invoice-title">
              <div class="d-flex align-items-center justify-content-center">
                <p class="mb-0 me-2 text-nowrap">Consumption Tax</p>
                <input type="number" v-model="localData.tax_rate" @input="updateTotals" class="form-control tax-input me-2">
                <span>%</span>
              </div>
            </td>
            <td class="text-end">{{ formatCurrency(calculateTax()) }}</td>
            <td></td>
          </tr>
          <tr>
            <td colspan="5" class="invoice-title">Total</td>
            <td class="text-end">
              {{ formatCurrency(calculateNoCarryoverTotal()) }}
              <input type="hidden" v-model="localData.billing_total_amount" readonly class="form-control">
            </td>
            <td></td>
          </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div class="mt-3">
      <!-- 振込先１(三菱UFJ銀行) -->
      <div class="d-flex">
        <div class="me-5">
          Remittance Address
        </div>
        <div class="ms-5">
          <p class="mb-0">Kabushikigaisha Soei Kikaku</p>
          <p class="mb-0">[(株)創英企画]</p>
          <p class="mb-0">Current Account {{ bankLabels.bank_01.account_no }}</p>
          <p class="mb-0">(当座預金 {{ bankLabels.bank_01.account_no }})</p>
          <p class="mb-0">{{ bankLabels.bank_01.name }} {{ bankLabels.bank_01.branch }}</p>
          <p class="mb-0">(三菱UFJ銀行 新宿中央支店)</p>
          <!-- hidden inputsはそのままでOK（日本語データ送信） -->
          <input type="hidden" v-model="bankLabels.bank_display_flg" class="form-control">
          <input type="hidden" v-model="bankLabels.bank_nm_01" class="form-control" placeholder="Bank Name">
          <input type="hidden" v-model="bankLabels.branch_nm_01" class="form-control" placeholder="Branch Name">
          <input type="hidden" v-model="bankLabels.account_type_01" class="form-control" placeholder="Account Type">
          <input type="hidden" v-model="bankLabels.account_no_01" class="form-control" placeholder="Account Number">
        </div>
      </div>
    </div>

    <!-- ボタンエリア -->
    <div class="btn-area">
      <a class="btn btn-secondary" @click="goBack">戻る</a>
      <button type="submit" class="btn btn-warning mx-1" :disabled="isSubmitting">
        {{ isSubmitting ? '更新中...' : '請求情報更新' }}
      </button>
    </div>

  </form>
</template>

<script setup>
import { calculateBillingAmounts } from '@/utils/billingAmounts';
import { reactive, computed, defineProps, defineEmits } from 'vue';
import BuildingSelectBox from '@/components/searchSelectBoxes/BuildingSelectBox.vue';
import CustomerSelectBox from '@/components/searchSelectBoxes/CustomerSelectBox.vue';
import CommaNumberInput from '@/components/shared/CommaNumberInput.vue';
import { buildConsecutiveBuildingGroups, stripMonthlyBillingLabel } from '@/utils/monthlyBillingDetails';

const props = defineProps({
  customerMonthlyBillingData: Object,
  buildings: Array,
  customers: Array,
  isSubmitting: Boolean
});

const emit = defineEmits(['add-row', 'remove-row', 'update-totals', 'go-back', 'submit']);

const bankLabels = computed(() => ({
  bank_01: {
    bank_display_flg: true,
    name: 'MUFG Bank, Ltd.',
    branch: 'Shinjuku Central Branch',
    type: 'Current Account',
    account_no: localData.account_no_01
  }
}));

const localData = reactive(props.customerMonthlyBillingData);
const groupedBillings = computed(() => buildConsecutiveBuildingGroups(localData.t_customer_monthly_billing_details || []));

const formatCurrency = (value) => {
  if (value) {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(value);
  }
  return '¥0';
};

const englishOptionMapped = [
  {
    billing_no: null,
    m_building_id: null,
    building_nm: null,
    work_type: 2,
    target_work_id: null,
    branch_billing_no: '',
    work_year: '',
    work_month: '',
    work_dt: '',
    work_content: 'Our Ref.No,',
    breakdown: '',
    bill_amount: '',
    sort_no: null
  },
  {
    billing_no: null,
    m_building_id: null,
    building_nm: null,
    work_type: 2,
    target_work_id: null,
    branch_billing_no: '',
    work_year: '',
    work_month: '',
    work_dt: '',
    work_content: 'Completion of the work',
    breakdown: '',
    bill_amount: '',
    sort_no: null
  }
];

const addDefaultRows = () => {
  if (!localData.t_customer_monthly_billing_details) return;

  // deep copy して追加（参照共有を防ぐ）
  const rows = englishOptionMapped.map(row => ({
    ...row,
    // 念のためユニーク性が必要なら追加
    sort_no: null
  }));

  localData.t_customer_monthly_billing_details.push(...rows);

  // 合計再計算が必要なら
  updateTotals();
};

const calculateTax = () => calculateBillingAmounts(localData).tax_amount;

const calculateNoCarryoverTotal = () => calculateBillingAmounts(localData).current_billing_amount;

const calculateGrandTotal = () => calculateBillingAmounts(localData).billing_total_amount;

const updateTotals = () => {
  emit('update-totals');
};
    
const handleSubmit = () => {
  emit('submit', localData);
};

const removeBillingRow = (index) => {
  emit('remove-row', index);
};

const goBack = () => {
  emit('go-back');
};
</script>

<style scoped>
.annual-work-cancel-row > td {
  background: #eef1f4 !important;
  color: #7b8491;
}

.annual-work-cancel-row textarea,
.annual-work-cancel-row input:not([type='checkbox']) {
  text-decoration: line-through;
}

.annual-cancel-check {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  padding: 7px 10px;
  border: 1px solid #f1aeb5;
  border-radius: 8px;
  background: #fff5f5;
  color: #b02a37;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}

.annual-cancel-check .form-check-input {
  margin: 0;
  cursor: pointer;
}

.fixed-fee-summary-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 9px;
  border: 1px solid #9ec5fe;
  border-radius: 999px;
  background: #e7f1ff;
  color: #084298;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.invoice-title{
  background-color: #77b8de;
  color: #fff;
}

/* ヘッダ */
.header h2 {
  font-weight: bold;
}

.header p {
  text-align: center;
  margin-bottom: 20px;
}

.annual-bill-customer-rep .relative{
  min-width: 500px;
  max-width: 500px;
}

.dear-nm-form{
  min-width: 360px;
}

.header .middle-title .billing-greeting,
.header .middle-title .billing-company-nm,
.header .middle-title .billing-company-representative,
.header .middle-title .billing-company-address {
  font-weight: bold;
}

.color-billing-company-orange {
  color: #ff6600;
}

.header .middle-title .billing-greeting{
  font-size: 20px;
  margin-left: 20px;
}
.header .billing-company-nm { font-size: 24px; }
.header .billing-company-representative { font-size: 18px; }
.header .company-regist-no { font-size: 16px; }

.total-billing-amount-area .total-billing-amount{
  display: inline-block;
}
.total-billing-amount-area .carryover-amount h5{
  min-width: 100px;
}
.total-billing-amount-area .carryover-amount input{
  max-width: 300px;
}

/* テーブルのスタイル */
table {
  border-collapse: collapse;
  width: 100%;
}

th, td {
  padding: 8px;
  border: 1px solid #000;
  text-align: center;
  font-size: 14px;
}
th span {
  font-size: 12px;
}
td input {
  font-size: 14px !important;
}
:deep(.form-control) {
  margin-bottom: 0;
  padding: 6px 12px;
  font-size: 14px;
}

.small-input {
  width: 50px;
}

.month-data-header {
  background-color: #f0f8ff;
  font-weight: bold;
}

.work-date {
  max-width: 160px;
}

.tax-input{
  max-width: 100px;
}

.bill-amount {
  max-width: 130px;
}

.no-resize {
  resize: none;
}

/* ボタンエリア */
.btn-area {
  text-align: right;
  margin-top: 20px;
}

.bank-checktitle{
  min-width: 120px;
  width: 120px;
  max-width: 120px;
}
</style>
